<script setup lang="ts">
import { ITicketPurchased } from '@/features/tickets/types'
import BKButton from '@/components/AppShared/BKButton.vue'
import { centsToDollars } from '@/services/currency'
import { ref } from 'vue'
import { AxiosError } from 'axios'
import { useRefundTicketMutation } from '@/features/tickets/api/queries'

const props = defineProps<{
  currentTicket: ITicketPurchased
}>()

const emit = defineEmits<{
  (e: 'close'): void
  (e: 'show-tier-change-interface'): void
}>()

const refundMutation = useRefundTicketMutation()

const handleShowChangeTier = () => {
  emit('show-tier-change-interface')
  emit('close')
}

const showSuccessMessage = ref(false)
const errorMessage = ref('')

const handleRefund = () => {
  if (refundMutation.isPending.value) return
  errorMessage.value = ''
  refundMutation.mutate(props.currentTicket.id, {
    onSuccess: () => {
      showSuccessMessage.value = true
    },
    onError: (err) => {
      // Surface backend refusals (e.g. policy window, already refunded) instead
      // of silently showing nothing. The success message must only appear on a
      // genuine success.
      const axiosErr = err as AxiosError<{ message?: string }>
      errorMessage.value = axiosErr.response?.data?.message || axiosErr.message || 'Refund failed. Please try again.'
    },
  })
}
</script>

<template>
  <div class="refund-ticket-interface">
    <div
      v-if="showSuccessMessage"
      class="success-message"
    >
      <p class="success-header">Refund in progress</p>
      <p class="success-text">
        We’ve sent a notice to the organizer of the event to confirm the refund. Please allow for a business week to
        receive refund.
      </p>
    </div>
    <template v-else>
      <div class="content">
        <p class="content-header">Refund Info</p>

        <div class="ticket-item">
          <p class="ticket-title">{{ currentTicket.title }}</p>
          <p
            v-if="currentTicket.description"
            class="ticket-description"
          >
            {{ currentTicket.description }}
          </p>
          <p class="ticket-price">– {{ centsToDollars(currentTicket.cost) }}</p>
        </div>

        <p class="refund-info-text">
          If you’d like to change the ticket tier instead of refund it
          <button
            class="change-tier-button"
            @click="handleShowChangeTier"
          >
            click here
          </button>
        </p>
      </div>
      <p
        v-if="errorMessage"
        class="refund-error-text"
      >
        {{ errorMessage }}
      </p>
      <div class="actions">
        <BKButton
          class="action-button"
          outlined
          @click="$emit('close')"
        >
          Cancel
        </BKButton>
        <BKButton
          class="action-button"
          color="var(--rasberry-red)"
          white-text
          :loading="refundMutation.isPending.value"
          @click="handleRefund"
        >
          Confirm Refund
        </BKButton>
      </div>
    </template>
  </div>
</template>

<style scoped lang="scss">
.refund-ticket-interface {
  display: flex;
  flex-direction: column;
  gap: 40px;
  justify-content: space-between;
  height: 100%;

  p {
    margin: 0;
  }

  .success-message {
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    height: 100%;
    max-width: 350px;
    margin: 0 auto;

    .success-header {
      margin-bottom: 8px;
      font-size: 24px;
      line-height: 1.3;
      color: var(--color-dark-800);
      font-weight: 700;
      text-align: center;
    }

    .success-text {
      margin-bottom: 40px;
      font-size: 16px;
      line-height: 1.3;
      color: var(--color-grey-300);
      text-align: center;
      text-wrap: balance;
    }
  }

  .content {
    display: flex;
    flex-direction: column;
  }

  .content-header {
    margin-bottom: 40px;
    font-size: 24px;
    line-height: 1.3;
    color: var(--color-dark-800);
    font-weight: 700;
    text-align: center;
  }

  .ticket-item {
    display: flex;
    flex-direction: column;
    gap: 4px;
    padding: 12px;
    margin-bottom: 10px;
    border-radius: 6px;
    border: 1px solid var(--color-grey-500);

    .ticket-title {
      font-size: 16px;
      line-height: 1.5;
      font-weight: 600;
      color: var(--color-dark-800);
    }

    .ticket-description {
      font-size: 16px;
      line-height: 1.5;
      color: var(--color-dark-800);
    }

    .ticket-price {
      font-size: 16px;
      line-height: 1.5;
      font-weight: 600;
      color: var(--color-dark-800);
    }
  }

  .refund-error-text {
    font-size: 14px;
    line-height: 1.5;
    color: var(--rasberry-red);
    text-align: center;
  }

  .refund-info-text {
    font-size: 14px;
    line-height: 1.5;
    color: var(--color-grey-300);

    .change-tier-button {
      cursor: pointer;
      color: var(--sky-blue);
      text-decoration: underline;
      font-weight: 500;
      font-size: 14px;
      line-height: 1.5;

      &:hover,
      &:focus-visible {
        filter: brightness(0.8);
      }

      &:active {
        filter: brightness(0.6);
      }
    }
  }

  .actions {
    display: flex;
    flex-wrap: wrap;
    justify-content: flex-end;
    gap: 10px;

    .action-button {
      flex: 1 1 0;
      min-width: fit-content;
    }
  }
}
</style>
